/**
 * 二元树 — GET /api/admin/binary-tree (PHP mirror, auto network root)
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { InputNumber, Space, Spin, Tree, Typography } from 'antd';
import type { DataNode } from 'antd/es/tree';
import { api } from '../../api/client';

interface TreeNode {
  id: number;
  username: string;
  level_code: string;
  position?: string | null;
  left?: TreeNode;
  right?: TreeNode;
}

type NetworkRoot = {
  id: number;
  username: string;
  level_code: string;
};

function toDataNodes(node: TreeNode): DataNode {
  const label = `${node.username} (#${node.id}) · ${node.level_code}${node.position ? ` [${node.position}]` : ''}`;
  return {
    title: label,
    key: String(node.id),
    children: buildChildren(node),
  };
}

function buildChildren(node: TreeNode): DataNode[] | undefined {
  const children: DataNode[] = [];
  if (node.left) children.push(toDataNodes(node.left));
  if (node.right) children.push(toDataNodes(node.right));
  return children.length > 0 ? children : undefined;
}

type Props = { embedded?: boolean };

export function BinaryTreePage({ embedded = false }: Props = {}) {
  const [maxDepth, setMaxDepth] = useState(6);

  const { data: networkRoot } = useQuery({
    queryKey: ['admin-binary-tree-network-root'],
    queryFn: () => api.get<NetworkRoot>('/api/admin/binary-tree/network-root'),
    staleTime: 300_000,
  });

  const rootId = networkRoot?.id;

  const treeQuery = useQuery({
    queryKey: ['admin-binary-tree-snapshot', rootId, maxDepth],
    queryFn: () =>
      api.get<TreeNode>(`/api/admin/binary-tree?user_id=${rootId}&max_depth=${maxDepth}`),
    enabled: !!rootId,
    staleTime: 60_000,
  });

  const treeData = useMemo((): DataNode[] => {
    const root = treeQuery.data;
    if (!root) return [];
    return [toDataNodes(root)];
  }, [treeQuery.data]);

  return (
    <div>
      {!embedded && <h2 style={{ marginTop: 0 }}>二元树</h2>}
      <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
        从 PHP 网体根节点
        {networkRoot ? (
          <>
            {' '}
            <Typography.Text strong>{networkRoot.username}</Typography.Text> (#{networkRoot.id})
          </>
        ) : (
          ' …'
        )}
        自动加载局部树预览（非 genesis/demo 种子数据）。
      </Typography.Paragraph>

      <Space style={{ marginBottom: 12 }} wrap align="start">
        <Space direction="vertical" size={4}>
          <span style={{ fontSize: 12, color: '#888' }}>最大深度 (1–8)</span>
          <InputNumber min={1} max={8} value={maxDepth} onChange={(v) => setMaxDepth(v ?? 6)} />
        </Space>
      </Space>

      {treeQuery.isLoading || !networkRoot ? (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin tip="加载网体根节点…" />
        </div>
      ) : treeData.length > 0 ? (
        <Tree showLine defaultExpandAll treeData={treeData} />
      ) : (
        <p style={{ color: '#999' }}>暂无树数据。</p>
      )}
    </div>
  );
}
